/**
 * livretTopolinoAM.js — Structure détaillée du livret de suivi
 * Permis AM · option quadricycle léger à moteur (FIAT Topolino).
 *
 * Utilisé par la page /livrets/topolino-am. Chaque item a un `id` unique
 * utilisé comme clé dans le state persisté par useLivretSync (locale + cloud).
 *
 * Valeurs possibles par item : 'todo' | 'wip' | 'ok' | 'retravailler'
 * (par défaut, item absent = 'todo').
 */

export const CHECK_STATES = [
  { key: 'todo',          label: 'À découvrir',            short: 'À découvrir',      color: '#94a3b8', dot: 'bg-slate-400/70' },
  { key: 'wip',           label: "En cours d'acquisition",  short: 'En cours',        color: '#f59e0b', dot: 'bg-amber-500' },
  { key: 'ok',            label: 'Acquis',                   short: 'Acquis',          color: '#22c55e', dot: 'bg-emerald-500' },
  { key: 'retravailler',  label: 'À retravailler',           short: 'À retravailler',  color: '#ef4444', dot: 'bg-rose-500' },
];

export function nextState(current) {
  const idx = CHECK_STATES.findIndex((s) => s.key === current);
  const next = idx < 0 ? 1 : (idx + 1) % CHECK_STATES.length;
  return CHECK_STATES[next].key;
}

/**
 * Sections du livret. Chaque section a :
 *   - id            : slug unique (pour ancres et dépliage)
 *   - kicker        : chip coloré (ex: "Mercredi · Découverte")
 *   - color         : couleur d'accent hex
 *   - title / subtitle
 *   - etapes[]      : liste d'étapes
 *
 * Chaque étape a :
 *   - id            : slug
 *   - num           : numéro ("1", "14", "Bilan")
 *   - title         : titre visible
 *   - groups[]      : sous-groupes de checkboxes (title + items[])
 *   - questions[]   : questions ouvertes optionnelles ({ id, label })
 *   - critere       : ligne "Critère" optionnelle ({ id, label })
 *   - exercice      : ligne "Exercice" optionnelle ({ id, label })
 */

export const LIVRET_TOPOLINO = {
  key: 'topolino-am-v1',
  meta: {
    title: 'Livret de suivi — Permis AM',
    subtitle: 'Option quadricycle léger à moteur — Fiat Topolino',
    tagline: 'Un mercredi pour maîtriser · un samedi pour circuler',
  },
  sections: [
    {
      id: 'mercredi',
      day: 'MERCREDI',
      color: '#0ea5e9',
      kicker: 'Mercredi · Découverte & maîtrise',
      title: 'Découverte du véhicule et maîtrise hors circulation',
      subtitle: 'Étapes 1 à 8 · en zone fermée',
      etapes: [
        {
          id: 'echanges', num: '1', title: 'Échanges avec l\'élève',
          groups: [
            {
              title: 'Représentations et attentes',
              items: [
                { id: 'e1-pourquoi',    label: "L'élève explique pourquoi il souhaite conduire une voiturette." },
                { id: 'e1-savoirs',    label: 'Il indique ce qu\'il pense savoir déjà faire.' },
                { id: 'e1-appre',      label: 'Il exprime ses éventuelles appréhensions.' },
                { id: 'e1-regles-am',  label: 'Il connaît les principales règles liées au permis AM.' },
                { id: 'e1-code-route', label: 'Il comprend que la voiturette reste un véhicule soumis aux règles du Code de la route.' },
              ],
            },
          ],
          questions: [
            { id: 'q1-securite', label: "« Pour toi, qu'est-ce qui est le plus important pour conduire une voiturette en sécurité ? »" },
          ],
        },
        {
          id: 'decouverte', num: '2', title: 'Découverte de la Topolino',
          groups: [
            {
              title: 'Avant de monter',
              items: [
                { id: 'e2-etat',        label: 'Observer l\'état général du véhicule.' },
                { id: 'e2-pneus',       label: 'Vérifier les pneumatiques.' },
                { id: 'e2-visibilite',  label: 'Vérifier la visibilité autour du véhicule.' },
                { id: 'e2-securite',    label: 'Identifier les éléments de sécurité.' },
                { id: 'e2-acces',       label: 'Identifier les différents accès au véhicule.' },
              ],
            },
            {
              title: 'À bord',
              items: [
                { id: 'e2-siege',        label: 'Siège correctement réglé.' },
                { id: 'e2-retros',       label: 'Rétroviseurs réglés.' },
                { id: 'e2-ceinture',     label: 'Ceinture attachée.' },
                { id: 'e2-position',     label: 'Position de conduite correcte.' },
                { id: 'e2-commandes',    label: 'Commandes identifiées.' },
                { id: 'e2-selecteur',    label: 'Sélecteur identifié.' },
                { id: 'e2-accel',        label: 'Pédale d\'accélérateur identifiée.' },
                { id: 'e2-frein',        label: 'Pédale de frein identifiée.' },
                { id: 'e2-stationnement',label: 'Frein de stationnement identifié.' },
                { id: 'e2-eclairage',    label: 'Commandes d\'éclairage identifiées.' },
                { id: 'e2-clignos',      label: 'Clignotants identifiés.' },
                { id: 'e2-avert',        label: 'Avertisseur sonore identifié.' },
                { id: 'e2-essuie',       label: 'Essuie-glaces / lave-glace identifiés.' },
                { id: 'e2-avant',        label: 'Marche avant identifiée.' },
                { id: 'e2-arriere',      label: 'Marche arrière identifiée.' },
              ],
            },
          ],
        },
        {
          id: 'avant-demarrer', num: '3', title: 'Avant de démarrer',
          hint: "L'élève réalise seul la vérification.",
          groups: [{
            items: [
              { id: 'e3-installation',  label: 'Installation.' },
              { id: 'e3-ceinture',      label: 'Ceinture.' },
              { id: 'e3-retros',        label: 'Rétroviseurs.' },
              { id: 'e3-portes',        label: 'Portes correctement fermées.' },
              { id: 'e3-environnement', label: 'Environnement contrôlé.' },
              { id: 'e3-pied-frein',    label: 'Pied sur le frein.' },
              { id: 'e3-mode',          label: 'Sélection du mode de déplacement.' },
              { id: 'e3-visuel',        label: 'Contrôle visuel avant de partir.' },
            ],
          }],
          critere: { id: 'e3-crit-autonomie', label: "L'élève réalise la procédure sans aide." },
        },
        {
          id: 'demarrer-arreter', num: '4', title: 'Démarrer et s\'arrêter',
          groups: [
            {
              title: 'Exercices',
              items: [
                { id: 'e4-progressif',    label: 'Démarrage progressif.' },
                { id: 'e4-accel-maitrise',label: 'Maîtrise de l\'accélérateur.' },
                { id: 'e4-allure-faible', label: 'Maintien d\'une allure faible.' },
                { id: 'e4-arret-prog',    label: 'Arrêt progressif.' },
                { id: 'e4-arret-precis',  label: 'Arrêt précis à un endroit demandé.' },
                { id: 'e4-redemarrage',   label: 'Redémarrage.' },
                { id: 'e4-immobilisation',label: 'Immobilisation complète.' },
              ],
            },
            {
              title: 'Critères',
              items: [
                { id: 'e4-crit-accel',   label: 'Pas d\'accélération excessive.' },
                { id: 'e4-crit-regard', label: 'Regard dirigé vers la zone d\'évolution.' },
                { id: 'e4-crit-freinage',label: 'Freinage progressif.' },
                { id: 'e4-crit-maitrise',label: 'Véhicule maîtrisé.' },
              ],
            },
          ],
        },
        {
          id: 'diriger', num: '5', title: 'Diriger la Topolino',
          groups: [{ items: [
            { id: 'e5-droite',    label: 'Tourner à droite.' },
            { id: 'e5-gauche',    label: 'Tourner à gauche.' },
            { id: 'e5-enchainer', label: 'Enchaîner plusieurs virages.' },
            { id: 'e5-trajectoire',label: 'Maintenir sa trajectoire.' },
            { id: 'e5-allure',    label: 'Adapter son allure avant le virage.' },
            { id: 'e5-regard-sortie', label: 'Regarder vers la sortie du virage.' },
            { id: 'e5-eviter-brusque', label: 'Eviter les corrections brusques.' },
          ] }],
          exercice: { id: 'e5-ex-regard', label: '« Regarde où tu veux aller, pas uniquement devant le capot. » — Réussi sans intervention.' },
        },
        {
          id: 'marche-arriere', num: '6', title: 'Marche arrière',
          groups: [{ items: [
            { id: 'e6-limites-visu',  label: 'Comprendre les limites de visibilité.' },
            { id: 'e6-env',           label: 'Contrôler l\'environnement.' },
            { id: 'e6-derriere',      label: 'Regarder derrière le véhicule.' },
            { id: 'e6-retros',        label: 'Utiliser les rétroviseurs.' },
            { id: 'e6-lentement',     label: 'Reculer lentement.' },
            { id: 'e6-traj',          label: 'Maintenir une trajectoire.' },
            { id: 'e6-arret',         label: 'S\'arrêter sur demande.' },
          ] }],
          critere: { id: 'e6-crit-doute', label: "L'élève sait interrompre la manœuvre en cas de doute." },
        },
        {
          id: 'freinage', num: '7', title: 'Freinage',
          groups: [{
            title: 'Exercices progressifs',
            items: [
              { id: 'e7-doux',        label: 'Freinage doux.' },
              { id: 'e7-appuye',      label: 'Freinage plus appuyé.' },
              { id: 'e7-precis',      label: 'Arrêt précis.' },
              { id: 'e7-distance',    label: 'Identification de la distance nécessaire pour s\'arrêter.' },
              { id: 'e7-influence',   label: 'Compréhension de l\'influence de la vitesse.' },
            ],
          }],
          questions: [
            { id: 'q7-vitesse-distance', label: '« Si tu vas plus vite, que se passe-t-il pour la distance nécessaire pour t\'arrêter ? »' },
          ],
        },
        {
          id: 'stationnement', num: '8', title: 'Stationnement',
          groups: [{ items: [
            { id: 'e8-bataille',  label: 'Stationnement en bataille.' },
            { id: 'e8-epi',       label: 'Stationnement en épi.' },
            { id: 'e8-creneau',   label: 'Créneau.' },
            { id: 'e8-controles', label: 'Contrôles avant la manœuvre.' },
            { id: 'e8-retros',    label: 'Utilisation des rétroviseurs.' },
            { id: 'e8-allure',    label: 'Allure très faible.' },
            { id: 'e8-env',       label: 'Contrôle de l\'environnement.' },
            { id: 'e8-immo',      label: 'Immobilisation correcte.' },
          ] }],
        },
      ],
    },

    {
      id: 'bilan-mercredi',
      day: 'BILAN MERCREDI',
      color: '#10b981',
      kicker: 'Fin de la séance du mercredi',
      title: "L'élève est-il prêt pour la circulation ?",
      subtitle: 'Maîtrise du véhicule + bilan pédagogique',
      etapes: [
        {
          id: 'bilan-maitrise', num: 'B1', title: 'Maîtrise du véhicule',
          groups: [{ items: [
            { id: 'bm-demarrer',  label: 'Démarre seul.' },
            { id: 'bm-sarrete',   label: 'S\'arrête seul.' },
            { id: 'bm-dirige',    label: 'Dirige correctement.' },
            { id: 'bm-freine',    label: 'Freine correctement.' },
            { id: 'bm-recule',    label: 'Recule correctement.' },
            { id: 'bm-allure',    label: 'Maîtrise l\'allure.' },
            { id: 'bm-controles', label: 'Effectue les contrôles.' },
            { id: 'bm-manoeuvre', label: 'Réalise une manœuvre simple.' },
          ] }],
          questions: [
            { id: 'bm-acquis', label: 'Points acquis :' },
            { id: 'bm-retravailler', label: 'Points à retravailler samedi :' },
          ],
          km: [
            { id: 'bm-km-effectue', label: 'Kilométrage effectué', unit: 'km' },
            { id: 'bm-autonomie-fin', label: 'Autonomie affichée à la fin', unit: 'km' },
          ],
        },
      ],
    },

    {
      id: 'samedi',
      day: 'SAMEDI',
      color: '#e11d48',
      kicker: 'Samedi · Circulation',
      title: 'Mise en circulation ouverte',
      subtitle: "Étapes 9 à 18 · avec les autres usagers",
      etapes: [
        {
          id: 'depart-circu', num: '9', title: 'Départ en circulation',
          hint: 'Avant de partir.',
          groups: [{ items: [
            { id: 'e9-instal',        label: 'Installation.' },
            { id: 'e9-ceinture',      label: 'Ceinture.' },
            { id: 'e9-retros',        label: 'Rétroviseurs.' },
            { id: 'e9-env',           label: 'Contrôle de l\'environnement.' },
            { id: 'e9-signal',        label: 'Signalisation comprise.' },
            { id: 'e9-allure',        label: 'Allure adaptée.' },
            { id: 'e9-explique',      label: "Élève capable d'expliquer ce qu'il va faire." },
          ] }],
        },
        {
          id: 'observer', num: '10', title: 'Observer',
          hint: 'À chaque situation, l\'élève doit apprendre à rechercher :',
          groups: [{ items: [
            { id: 'e10-devant',   label: 'Ce qui se passe devant.' },
            { id: 'e10-derriere', label: 'Ce qui se passe derrière.' },
            { id: 'e10-cotes',    label: 'Ce qui se passe sur les côtés.' },
            { id: 'e10-vuln',     label: 'Les usagers vulnérables.' },
            { id: 'e10-couper',   label: 'Les véhicules susceptibles de couper sa trajectoire.' },
            { id: 'e10-obstacles',label: 'Les obstacles.' },
            { id: 'e10-zones',    label: 'Les zones sans visibilité.' },
          ] }],
          questions: [
            { id: 'q10-reflex', label: '« Qu\'est-ce que tu vois ? Qu\'est-ce que tu ne vois pas ? Qu\'est-ce qui pourrait arriver ? »' },
          ],
        },
        {
          id: 'intersections', num: '11', title: 'Intersections',
          groups: [{ items: [
            { id: 'e11-simple',   label: 'Intersection simple.' },
            { id: 'e11-stop',     label: 'STOP.' },
            { id: 'e11-cedez',    label: 'Cédez-le-passage.' },
            { id: 'e11-droite',   label: 'Priorité à droite.' },
            { id: 'e11-feux',     label: 'Feux.' },
            { id: 'e11-ctrl',     label: 'Contrôle gauche / droite.' },
            { id: 'e11-avant-engager', label: 'Contrôle avant de s\'engager.' },
            { id: 'e11-allure',   label: 'Adaptation de l\'allure.' },
            { id: 'e11-priorite', label: 'Respect de la priorité.' },
            { id: 'e11-anticipe', label: 'Anticipation des autres usagers.' },
          ] }],
        },
        {
          id: 'changement-dir', num: '12', title: 'Changement de direction',
          groups: [{ items: [
            { id: 'e12-ctrl',     label: 'Contrôle.' },
            { id: 'e12-cligno',   label: 'Clignotant.' },
            { id: 'e12-place',    label: 'Placement.' },
            { id: 'e12-allure',   label: 'Adaptation de l\'allure.' },
            { id: 'e12-vuln',     label: 'Contrôle des usagers vulnérables.' },
            { id: 'e12-droite',   label: 'Virage à droite.' },
            { id: 'e12-gauche',   label: 'Virage à gauche.' },
            { id: 'e12-annulation',label: 'Annulation du clignotant si nécessaire.' },
          ] }],
        },
        {
          id: 'giratoire', num: '13', title: 'Giratoire',
          groups: [{ items: [
            { id: 'e13-ident',    label: 'Identification du giratoire.' },
            { id: 'e13-allure',   label: 'Réduction de l\'allure.' },
            { id: 'e13-obs',      label: 'Observation.' },
            { id: 'e13-place',    label: 'Placement adapté.' },
            { id: 'e13-ctrl',     label: 'Contrôle des autres usagers.' },
            { id: 'e13-priorite', label: 'Respect des priorités.' },
            { id: 'e13-cligno',   label: 'Clignotant approprié.' },
            { id: 'e13-sortie',   label: 'Sortie correctement signalée.' },
          ] }],
        },
        {
          id: 'diff-vitesse', num: '14', title: 'Différence de vitesse',
          hint: 'Avec une voiturette limitée à 45 km/h.',
          groups: [{ items: [
            { id: 'e14-comprend',  label: "L'élève comprend qu'il peut être beaucoup plus lent que certains autres usagers." },
            { id: 'e14-retros',    label: 'Il contrôle régulièrement ses rétroviseurs.' },
            { id: 'e14-anticipe',  label: 'Il anticipe l\'arrivée d\'un véhicule derrière.' },
            { id: 'e14-traj',      label: 'Il conserve une trajectoire stable.' },
            { id: 'e14-pression',  label: 'Il ne se laisse pas mettre sous pression.' },
            { id: 'e14-adapte',    label: 'Il adapte son comportement sans dépasser les limites du véhicule.' },
          ] }],
          questions: [
            { id: 'q14-derriere', label: '« Un véhicule arrive rapidement derrière toi : que dois-tu faire ? »' },
          ],
        },
        {
          id: 'usagers-vuln', num: '15', title: 'Usagers vulnérables',
          groups: [{ items: [
            { id: 'e15-pieton',   label: 'Piéton.' },
            { id: 'e15-enfant',   label: 'Enfant.' },
            { id: 'e15-cycliste', label: 'Cycliste.' },
            { id: 'e15-edpm',     label: 'Trottinette / EDPM.' },
            { id: 'e15-2rm',      label: 'Deux-roues motorisé.' },
            { id: 'e15-age',      label: 'Personne âgée ou vulnérable.' },
            { id: 'e15-porte',    label: 'Porte ouverte / véhicule stationné.' },
            { id: 'e15-imprevis', label: 'Anticipation d\'un déplacement imprévisible.' },
          ] }],
          critere: { id: 'e15-crit-spontane', label: "L'élève adapte spontanément son allure et sa trajectoire." },
        },
        {
          id: 'stationner-circu', num: '16', title: 'Stationnement en circulation',
          groups: [{ items: [
            { id: 'e16-recherche', label: 'Recherche d\'une place.' },
            { id: 'e16-ctrl',      label: 'Contrôle avant la manœuvre.' },
            { id: 'e16-cligno',    label: 'Clignotant.' },
            { id: 'e16-position',  label: 'Positionnement.' },
            { id: 'e16-lente',     label: 'Manœuvre lente.' },
            { id: 'e16-autour',    label: 'Contrôle autour du véhicule.' },
            { id: 'e16-immo',      label: 'Immobilisation.' },
            { id: 'e16-securise',  label: 'Sécurisation avant de quitter le véhicule.' },
          ] }],
        },
        {
          id: 'risques', num: '17', title: 'Situations à risque',
          hint: "L'élève doit être capable d'identifier :",
          groups: [{ items: [
            { id: 'e17-rue',       label: 'Véhicule sortant d\'une rue.' },
            { id: 'e17-place',     label: 'Véhicule sortant d\'une place.' },
            { id: 'e17-portiere',  label: 'Portière susceptible de s\'ouvrir.' },
            { id: 'e17-masque',    label: 'Piéton masqué.' },
            { id: 'e17-cycle-vis', label: 'Cycliste difficilement visible.' },
            { id: 'e17-rapide',    label: 'Véhicule arrivant rapidement.' },
            { id: 'e17-inter-vis', label: 'Intersection sans visibilité.' },
            { id: 'e17-gliss',     label: 'Chaussée glissante.' },
            { id: 'e17-mauvvis',   label: 'Mauvaise visibilité.' },
            { id: 'e17-etroite',   label: 'Route étroite.' },
            { id: 'e17-travaux',   label: 'Travaux.' },
          ] }],
        },
        {
          id: 'autonomie', num: '18', title: 'Autonomie',
          hint: "Pendant la fin de séance, l'enseignant intervient le moins possible.",
          groups: [{ items: [
            { id: 'e18-observe',  label: "L'élève observe seul." },
            { id: 'e18-allure',   label: "L'élève choisit son allure." },
            { id: 'e18-anticipe', label: "L'élève anticipe." },
            { id: 'e18-communique', label: "L'élève communique." },
            { id: 'e18-signal',   label: "L'élève respecte la signalisation." },
            { id: 'e18-inter',    label: "L'élève gère ses intersections." },
            { id: 'e18-chgmt',    label: "L'élève gère ses changements de direction." },
            { id: 'e18-explique', label: "L'élève sait expliquer ses décisions." },
            { id: 'e18-limite',   label: "L'élève sait reconnaître une situation qu'il ne maîtrise pas." },
          ] }],
        },
      ],
    },

    {
      id: 'autonomie-topolino',
      day: 'AUTONOMIE',
      color: '#8b5cf6',
      kicker: 'Suivi autonomie Topolino',
      title: '🔋 Suivi de l\'autonomie',
      subtitle: 'Kilométrage et batterie tout au long de la journée',
      etapes: [
        {
          id: 'autonomie-suivi', num: 'AT', title: 'Suivi autonomie',
          km: [
            { id: 'at-depart',    label: 'Autonomie affichée au départ', unit: 'km' },
            { id: 'at-1h',        label: 'Après 1 h', unit: 'km' },
            { id: 'at-2h',        label: 'Après 2 h', unit: 'km' },
            { id: 'at-retour',    label: 'Au retour', unit: 'km' },
            { id: 'at-total',     label: 'Kilométrage total', unit: 'km' },
          ],
          groups: [{
            title: 'Règle pédagogique',
            items: [
              { id: 'at-marge',    label: 'Je garde une marge suffisante.' },
              { id: 'at-pas-max',  label: "Je ne cherche pas à exploiter toute l'autonomie." },
              { id: 'at-conditions',label: 'Je prends en compte les conditions de circulation.' },
              { id: 'at-recharge', label: 'Je connais le point de recharge prévu si nécessaire.' },
            ],
          }],
        },
      ],
    },

    {
      id: 'bilan-final',
      day: 'BILAN FINAL',
      color: '#f59e0b',
      kicker: 'Bilan final',
      title: "L'élève sait :",
      subtitle: 'Synthèse des acquis à la fin de la formation',
      etapes: [
        {
          id: 'bilan-final-items', num: 'BF', title: 'Compétences globales',
          groups: [{ items: [
            { id: 'bf-installer',   label: 'S\'installer correctement.' },
            { id: 'bf-preparer',    label: 'Préparer le véhicule.' },
            { id: 'bf-demarrer',    label: 'Démarrer.' },
            { id: 'bf-arreter',     label: 'S\'arrêter.' },
            { id: 'bf-freiner',     label: 'Freiner.' },
            { id: 'bf-diriger',     label: 'Diriger.' },
            { id: 'bf-reculer',     label: 'Reculer.' },
            { id: 'bf-stationner',  label: 'Stationner.' },
            { id: 'bf-observer',    label: 'Observer.' },
            { id: 'bf-anticiper',   label: 'Anticiper.' },
            { id: 'bf-communiquer', label: 'Communiquer.' },
            { id: 'bf-priorites',   label: 'Respecter les priorités.' },
            { id: 'bf-allure',      label: 'Adapter son allure.' },
            { id: 'bf-autres',      label: 'Prendre en compte les autres usagers.' },
            { id: 'bf-danger',      label: 'Identifier les situations dangereuses.' },
            { id: 'bf-explique',    label: 'Expliquer ses choix de conduite.' },
          ] }],
          questions: [
            { id: 'note-forts',     label: "Points forts (note de l'enseignant) :" },
            { id: 'note-ameliorer', label: 'Points à améliorer :' },
            { id: 'note-conseil',   label: 'Conseil pour la suite :' },
          ],
        },
      ],
    },
  ],

  reflexes: [
    { emoji: '👀', title: 'JE REGARDE',     desc: 'Je recherche les informations utiles.' },
    { emoji: '🧠', title: "J'ANTICIPE",     desc: 'Je me demande ce qui pourrait arriver.' },
    { emoji: '🚗', title: "J'ADAPTE",       desc: 'J\'adapte mon allure et ma trajectoire.' },
    { emoji: '📢', title: 'JE COMMUNIQUE',  desc: 'Je rends mes intentions compréhensibles.' },
    { emoji: '🛑', title: 'JE RESTE MAÎTRE',desc: "Si j'ai un doute, je ralentis et je me donne du temps." },
  ],
};

export default LIVRET_TOPOLINO;

/** Extrait la liste plate de tous les item IDs "cochables" (booleans / états). */
export function getAllCheckItemIds() {
  const ids = [];
  LIVRET_TOPOLINO.sections.forEach((s) => {
    s.etapes.forEach((e) => {
      (e.groups || []).forEach((g) => (g.items || []).forEach((it) => ids.push(it.id)));
      if (e.critere) ids.push(e.critere.id);
      if (e.exercice) ids.push(e.exercice.id);
    });
  });
  return ids;
}
